/* Uptime Monitors: one row per company, rolling up its uptime monitors, open incidents and failed scenario runs.
   Per-monitor signals the company rollup misses (slow, stale, under 99% for 24h) still raise the row to watch, and
   expanding a row lists every monitor worst first. Monitors no company claims get their own rows so none are lost. */
import type { inferRouterOutputs } from "@trpc/server"
import { useCallback, useMemo } from "react"
import toast from "react-hot-toast"

import type { AppRouter } from "../../../../../aixinternal-api/src/router/_app"
import { trpc } from "../../../lib/trpc"
import { ago, worstVerdict } from "../homeDashboardModel"
import { EVerdict } from "../homeDashboard.types"
import type { IRowAction, IWidgetDefinition, IWidgetResult, IWidgetRow } from "../homeDashboard.types"
import { KeyValues } from "./WidgetParts"
import { WIDGET_QUERY_OPTIONS, useQueryWidget } from "./widgetQuery"

type RouterOutputs = inferRouterOutputs<AppRouter>
export type TMonitor = RouterOutputs["uptimeMonitors"]["list"][number]
type TCompany = RouterOutputs["companyHome"]["getOverview"]["companies"][number]
interface IUptimeData {
  overview: RouterOutputs["companyHome"]["getOverview"]
  monitors: TMonitor[]
}
type TCheckMonitors = (monitors: TMonitor[]) => IRowAction[]

/** Up, but below this 24h uptime, is worth a look */
const UPTIME_WATCH_PCT = 99
/** Up, but slower than this share of the monitor's timeout, is worth a look */
const SLOW_LATENCY_RATIO = 0.5
/** No check for this many intervals means the runner stopped checking it */
const STALE_INTERVALS = 3
const SECOND_MS = 1000
const PERCENTILE_50 = 0.5
const PERCENTILE_95 = 0.95

const VERDICT_RANK: Record<EVerdict, number> = {
  [EVerdict.ACT]: 0,
  [EVerdict.WATCH]: 1,
  [EVerdict.UNKNOWN]: 2,
  [EVerdict.CLEAR]: 3,
}

const formatMs = (ms: number | null | undefined) => {
  if (ms === null || ms === undefined) return "—"
  return ms < SECOND_MS ? `${ms}ms` : `${(ms / SECOND_MS).toFixed(1)}s`
}

const percentile = (sorted: number[], p: number): number | undefined =>
  sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))] : undefined

const isStale = (monitor: TMonitor, now: number) =>
  !!monitor.lastCheck &&
  now - new Date(monitor.lastCheck.checkedAt).getTime() > monitor.intervalSeconds * SECOND_MS * STALE_INTERVALS

/** One rule for monitor health, shared with the Uptime Monitors pages */
export const monitorState = (monitor: TMonitor, now: number): { verdict: EVerdict; pill: string } => {
  if (monitor.status === "down") return { verdict: EVerdict.ACT, pill: monitor.hasOpenIncident ? "incident" : "down" }
  if (monitor.status === "unknown") return { verdict: EVerdict.UNKNOWN, pill: "no checks" }
  if (isStale(monitor, now)) return { verdict: EVerdict.WATCH, pill: "stale" }
  if (monitor.uptime24h < UPTIME_WATCH_PCT) return { verdict: EVerdict.WATCH, pill: `${monitor.uptime24h}%` }
  const latency = monitor.lastCheck?.latencyMs ?? 0
  if (latency > monitor.timeoutMs * SLOW_LATENCY_RATIO) return { verdict: EVerdict.WATCH, pill: "slow" }
  return { verdict: EVerdict.CLEAR, pill: "up" }
}

const monitorLine = (monitor: TMonitor, now: number) => {
  const { pill } = monitorState(monitor, now)
  const check = monitor.lastCheck
  const status = check?.statusCode ? ` · ${check.statusCode}` : ""
  const error = monitor.status === "down" && check?.error ? ` · ${check.error}` : ""
  return `${pill}${status} · ${formatMs(check?.latencyMs)} · ${monitor.uptime24h}% 24h${error}`
}

/** `t` re-checks the monitors that need it (all of them when nothing is wrong) and saves the results. */
const useCheckMonitors = (): TCheckMonitors => {
  const utils = trpc.useUtils()
  const check = trpc.uptimeMonitors.triggerCheck.useMutation()
  const { mutateAsync, isLoading } = check
  return useCallback(
    (monitors: TMonitor[]) => {
      if (!monitors.length) return []
      const run = async () => {
        const results = await Promise.allSettled(monitors.map((m) => mutateAsync({ id: m.id })))
        const down = results.flatMap((r) =>
          r.status === "fulfilled" && !r.value.success ? [r.value.monitor.name] : [],
        )
        const failed = results.filter((r) => r.status === "rejected").length
        if (down.length) toast.error(`Still down: ${down.join(", ")}`)
        else if (failed) toast.error(`${failed} check${failed > 1 ? "s" : ""} failed to run`)
        else toast.success(`${monitors.length} check${monitors.length > 1 ? "s" : ""} passed`)
        void utils.uptimeMonitors.list.invalidate()
        void utils.companyHome.getOverview.invalidate()
      }
      const label = monitors.length === 1 ? "Check now" : `Check ${monitors.length} now`
      return [{ key: "t", label, disabled: isLoading, run: () => void run() }]
    },
    [mutateAsync, isLoading, utils],
  )
}

const needsCheck = (monitors: TMonitor[], now: number) => {
  const flagged = monitors.filter((m) => monitorState(m, now).verdict !== EVerdict.CLEAR)
  return flagged.length ? flagged : monitors
}

const COMPANY_VERDICT: Record<string, EVerdict> = {
  degraded: EVerdict.ACT,
  attention: EVerdict.WATCH,
  healthy: EVerdict.CLEAR,
}

/** Same match as companyHome: a monitor's service is the company's monitor project key or its slug. */
const monitorsFor = (item: TCompany, monitors: TMonitor[]) => {
  const keys = new Set([item.monitorProject?.project ?? item.company.slug, item.company.slug])
  return monitors.filter((m) => keys.has(m.service))
}

const companyPill = (item: TCompany, worst: TMonitor | undefined, now: number): string => {
  const { openIncidents, failedUptimeMonitors, recentFailedRuns } = item.counts
  if (failedUptimeMonitors) return `${failedUptimeMonitors} down`
  if (openIncidents) return `${openIncidents} incident${openIncidents > 1 ? "s" : ""}`
  if (recentFailedRuns) return "scenario failed"
  if (worst) return monitorState(worst, now).pill
  return item.status
}

const companySub = (item: TCompany, monitors: TMonitor[]): string => {
  const down = monitors.filter((m) => m.status === "down")
  if (down.length) return down.map((m) => `${m.name}${m.lastCheck?.error ? `: ${m.lastCheck.error}` : ""}`).join(" · ")
  const avg = monitors.length ? monitors.reduce((sum, m) => sum + m.uptime24h, 0) / monitors.length : null
  return [
    `${monitors.length} monitor${monitors.length === 1 ? "" : "s"}`,
    avg === null ? null : `${avg.toFixed(2)}% 24h`,
    item.counts.openIncidents ? `${item.counts.openIncidents} incidents` : null,
    item.counts.enabledScenarios ? `${item.counts.enabledScenarios} scenarios` : null,
  ]
    .filter(Boolean)
    .join(" · ")
}

const companyRow = (item: TCompany, all: TMonitor[], now: number, checkMonitors: TCheckMonitors): IWidgetRow => {
  const monitors = monitorsFor(item, all).sort(
    (a, b) => VERDICT_RANK[monitorState(a, now).verdict] - VERDICT_RANK[monitorState(b, now).verdict],
  )
  const worst = monitors.find((m) => monitorState(m, now).verdict !== EVerdict.CLEAR)
  const verdict = worstVerdict(
    [COMPANY_VERDICT[item.status] ?? EVerdict.UNKNOWN, ...monitors.map((m) => monitorState(m, now).verdict)],
    EVerdict.CLEAR,
  )
  return {
    key: item.company.id,
    company: item.company.slug,
    verdict,
    pill: { text: companyPill(item, worst, now), verdict },
    title: item.company.name,
    sub: companySub(item, monitors),
    meta: item.latest.lastUptimeCheckAt ? `checked ${ago(item.latest.lastUptimeCheckAt, now)}` : "no checks",
    quiet: verdict === EVerdict.CLEAR,
    details: (
      <KeyValues
        pairs={[
          ...monitors.map((m): [string, string] => [m.name, monitorLine(m, now)]),
          ["Failed scenario runs 24h", item.counts.recentFailedRuns ? String(item.counts.recentFailedRuns) : null],
          ["Last incident", item.latest.latestIncidentAt ? ago(item.latest.latestIncidentAt, now) : null],
          ["Monitor project", item.monitorProject?.displayName ?? null],
        ]}
      />
    ),
    href: `/devtools/uptime?company=${item.company.slug}`,
    hrefLabel: `${item.company.name} monitors`,
    actions: checkMonitors(needsCheck(monitors, now)),
  }
}

/** A monitor whose service matches no company: shown on its own so a failure there is never hidden. */
const orphanRow = (monitor: TMonitor, now: number, checkMonitors: TCheckMonitors): IWidgetRow => {
  const { verdict, pill } = monitorState(monitor, now)
  return {
    key: monitor.id,
    company: monitor.service,
    verdict,
    pill: { text: pill, verdict },
    title: monitor.name,
    sub: `${monitor.service} (no company) · ${monitorLine(monitor, now)}`,
    meta: monitor.lastCheck ? ago(monitor.lastCheck.checkedAt, now) : "never checked",
    quiet: verdict === EVerdict.CLEAR,
    details: <KeyValues pairs={[["URL", monitor.url]]} />,
    href: `/devtools/uptime/${monitor.id}`,
    hrefLabel: "Open monitor",
    actions: checkMonitors([monitor]),
  }
}

const renderUptime = ({ overview, monitors: all }: IUptimeData, checkMonitors: TCheckMonitors): IWidgetResult => {
  const now = Date.now()
  const monitors = all.filter((m) => m.enabled)
  const claimed = new Set(overview.companies.flatMap((item) => monitorsFor(item, monitors).map((m) => m.id)))
  const rows = [
    ...overview.companies.map((item) => companyRow(item, monitors, now, checkMonitors)),
    ...monitors.filter((m) => !claimed.has(m.id)).map((m) => orphanRow(m, now, checkMonitors)),
  ]
  const down = monitors.filter((m) => m.status === "down").length
  const latencies = monitors
    .map((m) => m.lastCheck?.latencyMs)
    .filter((ms): ms is number => typeof ms === "number")
    .sort((a, b) => a - b)
  const avgUptime = monitors.length ? monitors.reduce((sum, m) => sum + m.uptime24h, 0) / monitors.length : 100
  const slowest = monitors.reduce<TMonitor | undefined>(
    (max, m) => ((m.lastCheck?.latencyMs ?? 0) > (max?.lastCheck?.latencyMs ?? 0) ? m : max),
    undefined,
  )
  const paused = all.length - monitors.length

  return {
    verdict: worstVerdict(rows.map((r) => r.verdict)),
    count: down ? `${down} of ${monitors.length} monitors down` : `${monitors.length} monitors up`,
    lead: monitors.length ? (
      <KeyValues
        pairs={[
          ["Uptime 24h", `${avgUptime.toFixed(2)}% avg`],
          [
            "Latency",
            `p50 ${formatMs(percentile(latencies, PERCENTILE_50))} · p95 ${formatMs(percentile(latencies, PERCENTILE_95))}`,
          ],
          ["Slowest", slowest?.lastCheck ? `${slowest.name} ${formatMs(slowest.lastCheck.latencyMs)}` : null],
        ]}
      />
    ) : undefined,
    rows,
    quietLabel: `${rows.filter((r) => r.quiet).length} healthy`,
    empty: "No active companies or monitors.",
    foot: [
      `${overview.summary.recentFailedRuns} failed scenario runs in 24h`,
      paused ? `${paused} monitors paused` : null,
      `generated ${ago(overview.generatedAt, now)}`,
    ]
      .filter(Boolean)
      .join(" · "),
  }
}

/** Two independent requests (each skipBatch); the widget renders once both have data and refreshes both. */
const useUptimeData = () => {
  const overview = trpc.companyHome.getOverview.useQuery(undefined, WIDGET_QUERY_OPTIONS)
  const monitors = trpc.uptimeMonitors.list.useQuery(undefined, WIDGET_QUERY_OPTIONS)
  const data = useMemo<IUptimeData | undefined>(
    () => (overview.data && monitors.data ? { overview: overview.data, monitors: monitors.data } : undefined),
    [overview.data, monitors.data],
  )
  return {
    data,
    error: overview.error ?? monitors.error,
    isLoading: overview.isLoading || monitors.isLoading,
    isFetching: overview.isFetching || monitors.isFetching,
    dataUpdatedAt: Math.min(overview.dataUpdatedAt, monitors.dataUpdatedAt),
    refetch: () => {
      void overview.refetch()
      void monitors.refetch()
    },
  }
}

/** Keeps the old Company health id so saved placement, snoozes and views carry over. */
export const uptimeMonitorsWidget: IWidgetDefinition = {
  id: "health",
  title: "Uptime Monitors",
  description: "Per-company uptime, incidents and scenario runs; failing monitors first",
  columns: { 2: 2, 3: 3, 4: 3, 5: 4, 6: 5 },
  link: { href: "/devtools/uptime", label: "Open Uptime Monitors" },
  useWidget: () => {
    const checkMonitors = useCheckMonitors()
    const render = useCallback((data: IUptimeData) => renderUptime(data, checkMonitors), [checkMonitors])
    return useQueryWidget(useUptimeData(), render)
  },
}
